import { type LabelMap, labelIdentity } from "../api/labelsApi";
import type { Project } from "../api/projectsApi";

// What a project filter offers (ONL-209). One entry per name, because someone
// who named an https clone and an ssh clone the same has said they are one.
// The name stops here: the server is sent keys, so a CLI label (ONL-197) can
// only change the grouping.

/** One choice in a filter. */
export interface ProjectEntry {
	name: string;
	/** What `project` carries to the API, for every key under this name. */
	values: string[];
}

/** The user's name for a key, or the key. `machineId` is null when remote. */
export function projectName(
	key: string,
	machineId: string | null,
	labels: LabelMap,
): string {
	return labels.get(labelIdentity(key, machineId)) ?? key;
}

// The shape the server filters on. Sync accepts other keys, but a request
// carrying one is a 400, and sharing a name would take its neighbors with it.
const FILTERABLE_KEY = /^[0-9a-f]{12}$/;

/**
 * An entry's keys alone, for a read with no machine. A lesson stores none, so
 * the same root key on two machines is one thing to it.
 */
export function entryKeys(entry: ProjectEntry): string[] {
	return [...new Set(entry.values.map((value) => value.slice(-12)))];
}

export function projectEntries(
	projects: Project[],
	labels: LabelMap,
): ProjectEntry[] {
	const entries = new Map<string, string[]>();
	for (const { project_key: key, machine_id: machineId } of projects) {
		if (!FILTERABLE_KEY.test(key)) continue;
		const name = projectName(key, machineId, labels);
		const value = machineId === null ? key : `${machineId}:${key}`;
		entries.set(name, [...(entries.get(name) ?? []), value]);
	}
	return [...entries]
		.map(([name, values]) => ({ name, values }))
		.sort((a, b) =>
			a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
		);
}
